import { HttpService, TaggedUrl, Tags } from '@skylab-shared/core';
import { BackgroundTaskKind, ImportDuplicates, ImportStatus, ImportedItemErrorKind, ImportedItemStatus } from '@skylab-shared/dataModel';
import { MS_PER_MINUTE, ResponseUtils, fillPlaceholders } from '@skylab-shared/utils';

export interface ImportedItem {
    id: number;
    name: string;
    status: ImportedItemStatus;
    errors: ImportedItemError[];
}

export interface ImportedItemError {
    code: ImportedItemErrorKind;
    parameter: string;
    type: ImportedItemStatus;
}

export interface ImportData {
    id: number;
    kind: BackgroundTaskKind;
    result: {
        data: ImportedItem[];
        imported: number;
        notImported: number;
    };
    status: ImportStatus;
}

export type DocumentImportedXmlActionParams = {
    documentId: number,
    version: string
}

export class ImportRepositoryBase {
    public constructor(
        private readonly $http: HttpService
    ) { }

    public async getImportDataAsync(operationId: number) {
        const response = await this.$http.get<ImportData>(fillPlaceholders(TaggedUrl.join('backgroundTasks/:id', [Tags.Api]), { id: operationId }));
        return {
            isSuccessfull: ResponseUtils.isStatusSuccess(response.status),
            data: response.data
        };
    }

    public async importClientsAsync(data: FormData, params: { duplicates: ImportDuplicates; identification: string; }): Promise<{ isSuccessfull: boolean, data: unknown }> {
        const response = await this.$http.post(
            TaggedUrl.join('clients/import', [Tags.Api]),
            data,
            {
                headers: { 'Content-Type': undefined },
                method: 'POST',
                params: {
                    mode: params.duplicates,
                    identification: params.identification
                },
                transformRequest: [],
                timeout: 1 * MS_PER_MINUTE
            }
        );
        return {
            isSuccessfull: ResponseUtils.isStatusSuccess(response.status),
            data: response.data
        };
    }

    public async importProductsAsync(data: FormData, params: { duplicates: ImportDuplicates }): Promise<{ isSuccessfull: boolean, data: unknown }> {
        const response = await this.$http.post(
            TaggedUrl.join('products/import', [Tags.Api]),
            data,
            {
                headers: { 'Content-Type': undefined },
                params: {
                    mode: params.duplicates,
                },
                transformRequest: [],
                timeout: 1 * MS_PER_MINUTE
            }
        );
        return {
            isSuccessfull: ResponseUtils.isStatusSuccess(response.status),
            data: response.data
        };
    }

    public async importKsefXmlAsync(data: FormData, params: DocumentImportedXmlActionParams): Promise<{ isSuccessfull: boolean, data: DocumentImportedXmlActionParams }> {
        try {
            const response = await this.$http.put(
                TaggedUrl.join('documents/import/xml', [Tags.Api, Tags.NotHandle404]),
                data,
                {
                    headers: { 'Content-Type': undefined },
                    transformRequest: [],
                    timeout: 1 * MS_PER_MINUTE,
                    params
                }
            );

            return {
                isSuccessfull: true,
                data: response.data
            };
        } catch (err) {
            return {
                isSuccessfull: false,
                data: (err as any)?.data
            };
        }
    }

    public async deleteImportedKsefXmlAsync(params: DocumentImportedXmlActionParams): Promise<DocumentImportedXmlActionParams> {
        const response = await this.$http.delete(
            TaggedUrl.join('documents/import/xml', [Tags.Api]),
            {
                params
            }
        );
        return response.data;
    }
}
